/**
 * Operation Form Page
 * 
 * Create or edit an operation.
 * Supports transfer mode when multiple accounts exist.
 * 
 * @validates Requirements 8.1, 8.8 - Operation management
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useOperationStore } from '../store/operationStore';
import { useAccountStore } from '../store/accountStore';
import { useCategoryStore } from '../store/categoryStore';
import { usePaymentMethodStore } from '../store/paymentMethodStore';
import { useProjectStore } from '../store/projectStore';
import { Card, Button, Input } from '../components/ui';
import CategorySelector from '../components/CategorySelector';
import CounterpartySelector from '../components/common/CounterpartySelector';
import CheckNumberInput from '../components/CheckNumberInput';
import { suggestionService } from '../services/suggestion.service';
import { reconciliationService } from '../services/reconciliation.service';
import type { OperationDirection, OperationStatus } from '@budgira/shared';

interface OperationFormData {
  direction: OperationDirection;
  paymentDate: string;
  accountingDate: string;
  amount: string;
  counterpartyName: string;
  categoryName: string;
  paymentMethodName: string;
  paymentDetails: string;
  reference: string;
  comment: string;
  status: OperationStatus;
  // IDs loaded from existing operation (for edit mode)
  counterpartyId?: string;
  paymentMethodId?: string;
  categoryId?: string;
  projectId?: string;
  // Checkbook for auto-numbering checks
  checkbookId?: string;
}

interface TransferFormData {
  paymentDate: string;
  accountingDate: string;
  amount: string;
  destinationAccountId: string;
  debitCategoryId: string;
  creditCategoryId: string;
  reference: string;
  comment: string;
  status: OperationStatus;
}

const OperationFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId: urlAccountId, operationId } = useParams<{ accountId?: string; operationId?: string }>();
  const { selectedAccount, selectAccount, accounts, fetchAccounts } = useAccountStore();
  const { createOperation, createTransfer, updateOperation, getOperation, isLoading } = useOperationStore();
  const { categories, fetchCategories } = useCategoryStore();
  const { paymentMethods, fetchPaymentMethods } = usePaymentMethodStore();
  const { projects, fetchProjects } = useProjectStore();
  
  const isEditMode = !!operationId;
  
  // Get prefill data from navigation state (e.g., from reconciliation)
  const locationState = location.state as { 
    prefill?: {
      paymentDate?: string;
      amount?: number;
      direction?: OperationDirection;
      reference?: string;
      counterpartyName?: string;
    }; 
    reconciliation?: {
      sessionId: string;
      lineId: string;
    };
    returnTo?: string;
  } | null;
  
  const prefillData = locationState?.prefill;
  const reconciliationData = locationState?.reconciliation;
  const returnTo = locationState?.returnTo;
  
  // Transfer mode - only available in create mode with 2+ accounts
  const [isTransferMode, setIsTransferMode] = useState(false);
  const canTransfer = !isEditMode && accounts.length >= 2;

  const today = new Date().toISOString().split('T')[0] || '';
  
  // In edit mode, accountId comes from the loaded operation
  // In create mode, accountId comes from the URL
  const [effectiveAccountId, setEffectiveAccountId] = useState<string | undefined>(urlAccountId);
  
  const [formData, setFormData] = useState<OperationFormData>({
    direction: prefillData?.direction || 'debit',
    paymentDate: prefillData?.paymentDate || today,
    accountingDate: '',
    amount: prefillData?.amount?.toString() || '',
    counterpartyName: prefillData?.counterpartyName || '',
    categoryName: '',
    paymentMethodName: 'Carte bancaire',
    paymentDetails: '',
    reference: prefillData?.reference || '',
    comment: '',
    status: 'unpointed',
  });

  const [transferData, setTransferData] = useState<TransferFormData>({
    paymentDate: today,
    accountingDate: '',
    amount: '',
    destinationAccountId: '',
    debitCategoryId: '',
    creditCategoryId: '',
    reference: '',
    comment: '',
    status: 'unpointed',
  });

  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingOperation, setIsLoadingOperation] = useState(isEditMode);

  // Fetch category suggestion when counterparty changes (create mode only)
  const fetchCategorySuggestion = useCallback(async (counterpartyName: string, direction: OperationDirection, accountId?: string) => {
    if (!counterpartyName.trim() || isEditMode) return;
    
    try {
      const result = await suggestionService.suggestCategoryByName(counterpartyName, direction, accountId);
      if (result.suggestion) {
        // Auto-fill category if not already set
        setFormData(prev => {
          if (!prev.categoryId && !prev.categoryName.trim()) {
            return {
              ...prev,
              categoryId: result.suggestion!.categoryId,
              categoryName: result.suggestion!.categoryName,
            };
          }
          return prev;
        });
      }
    } catch {
      // Silently fail - suggestion is optional
    }
  }, [isEditMode]);

  // Debounced category suggestion on counterparty change
  useEffect(() => {
    if (isEditMode || !formData.counterpartyName.trim()) return;
    
    const timer = setTimeout(() => {
      fetchCategorySuggestion(formData.counterpartyName, formData.direction, effectiveAccountId);
    }, 500); // Wait 500ms after typing stops
    
    return () => clearTimeout(timer);
  }, [formData.counterpartyName, formData.direction, fetchCategorySuggestion, isEditMode, effectiveAccountId]);

  useEffect(() => {
    if (accounts.length === 0) {
      fetchAccounts();
    }
  }, [accounts.length, fetchAccounts]);

  // Fetch categories early
  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories();
    }
  }, [categories.length, fetchCategories]);

  // Fetch payment methods
  useEffect(() => {
    if (paymentMethods.length === 0) {
      fetchPaymentMethods();
    }
  }, [paymentMethods.length, fetchPaymentMethods]);

  // Fetch projects for project selector (include archived to show project on existing operations)
  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects(true); // Force include archived projects
    }
  }, [projects.length, fetchProjects]);

  // In create mode, select account from URL
  useEffect(() => {
    if (urlAccountId && !isEditMode) {
      selectAccount(urlAccountId);
      setEffectiveAccountId(urlAccountId);
    }
  }, [urlAccountId, selectAccount, isEditMode]);

  // In edit mode, load operation and then select its account
  useEffect(() => {
    if (isEditMode && operationId) {
      loadOperation(operationId);
    }
  }, [isEditMode, operationId]);

  // Set default destination account when transfer mode is enabled
  useEffect(() => {
    if (isTransferMode && !transferData.destinationAccountId && accounts.length >= 2 && effectiveAccountId) {
      const otherAccounts = accounts.filter(a => a.id !== effectiveAccountId);
      const firstOtherAccount = otherAccounts[0];
      if (firstOtherAccount) {
        setTransferData(prev => ({ ...prev, destinationAccountId: firstOtherAccount.id }));
      }
    }
  }, [isTransferMode, accounts, effectiveAccountId, transferData.destinationAccountId]);

  const loadOperation = async (id: string) => {
    setIsLoadingOperation(true);
    try {
      const operation = await getOperation(id);
      if (operation) {
        // Set the effective account ID from the operation
        setEffectiveAccountId(operation.accountId);
        // Select the account in the store
        selectAccount(operation.accountId);
        
        setFormData({
          direction: operation.direction,
          paymentDate: operation.paymentDate,
          accountingDate: operation.accountingDate || '',
          amount: operation.amount.toString(),
          counterpartyName: operation.counterparty?.name || '',
          categoryName: operation.categories?.[0]?.category?.name || '',
          paymentMethodName: operation.paymentMethod?.name || 'Carte bancaire',
          paymentDetails: operation.paymentDetails || '',
          reference: operation.reference || '',
          comment: operation.comment || '',
          status: operation.status,
          // Store IDs for edit mode
          counterpartyId: operation.counterpartyId,
          paymentMethodId: operation.paymentMethodId,
          categoryId: operation.categories?.[0]?.categoryId,
          projectId: operation.projectId,
        });
      }
    } finally {
      setIsLoadingOperation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    console.log('[OperationFormPage] handleSubmit called', { isTransferMode, effectiveAccountId });

    if (!effectiveAccountId) {
      setFormError(t('operations.selectAccount'));
      return;
    }

    // Transfer mode submission
    if (isTransferMode) {
      if (!transferData.amount || parseFloat(transferData.amount) <= 0) {
        setFormError(t('operations.form.errors.amountRequired'));
        return;
      }
      if (!transferData.paymentDate) {
        setFormError(t('operations.form.errors.dateRequired'));
        return;
      }
      if (!transferData.destinationAccountId) {
        setFormError(t('transfer.errors.destinationRequired'));
        return;
      }
      if (!transferData.debitCategoryId) {
        setFormError(t('transfer.errors.debitCategoryRequired'));
        return;
      }
      if (!transferData.creditCategoryId) {
        setFormError(t('transfer.errors.creditCategoryRequired'));
        return;
      }

      setIsSaving(true);
      try {
        console.log('[OperationFormPage] Creating transfer:', {
          sourceAccountId: effectiveAccountId,
          destinationAccountId: transferData.destinationAccountId,
          amount: parseFloat(transferData.amount),
          debitCategoryId: transferData.debitCategoryId,
          creditCategoryId: transferData.creditCategoryId,
        });
        
        const result = await createTransfer({
          sourceAccountId: effectiveAccountId,
          destinationAccountId: transferData.destinationAccountId,
          paymentDate: transferData.paymentDate,
          accountingDate: transferData.accountingDate || undefined,
          amount: parseFloat(transferData.amount),
          debitCategoryId: transferData.debitCategoryId,
          creditCategoryId: transferData.creditCategoryId,
          reference: transferData.reference.trim() || undefined,
          comment: transferData.comment.trim() || undefined,
          status: transferData.status,
        });

        console.log('[OperationFormPage] Transfer result:', result);

        if (result) {
          // Navigate back (page is preserved in returnTo URL)
          const targetUrl = returnTo || `/accounts/${effectiveAccountId}/operations`;
          navigate(targetUrl);
        } else {
          // Get error from store if result is null
          const storeError = useOperationStore.getState().error;
          setFormError(storeError || 'Une erreur est survenue lors de la création du transfert');
        }
      } catch (err) {
        console.error('[OperationFormPage] Transfer error:', err);
        setFormError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Normal operation submission
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setFormError(t('operations.form.errors.amountRequired'));
      return;
    }
    if (!formData.paymentDate) {
      setFormError(t('operations.form.errors.dateRequired'));
      return;
    }
    if (!formData.counterpartyName.trim()) {
      setFormError(t('operations.form.errors.counterpartyRequired'));
      return;
    }
    if (!formData.categoryId && !formData.categoryName.trim()) {
      setFormError(t('operations.form.errors.categoryRequired'));
      return;
    }
    if (!formData.paymentMethodId && !formData.paymentMethodName.trim()) {
      setFormError(t('operations.form.errors.paymentMethodRequired'));
      return;
    }

    setIsSaving(true);
    try {
      if (isEditMode && operationId) {
        console.log('[OperationFormPage] Updating operation:', operationId);
        // Edit mode: use IDs for the update request
        const newAmount = parseFloat(formData.amount);
        
        // Build categories array - always include if we have a categoryId to ensure validation passes
        let categories: Array<{ categoryId: string; amount: number }> | undefined;
        if (formData.categoryId) {
          categories = [{ categoryId: formData.categoryId, amount: newAmount }];
        }
        
        const updateData = {
          direction: formData.direction,
          paymentDate: formData.paymentDate,
          accountingDate: formData.accountingDate || null, // Send null to clear the date
          amount: newAmount,
          paymentDetails: formData.paymentDetails.trim() || null, // Send null to clear
          reference: formData.reference.trim() || null, // Send null to clear
          comment: formData.comment.trim() || null, // Send null to clear
          status: formData.status,
          counterpartyId: formData.counterpartyId,
          counterpartyName: formData.counterpartyName.trim() || undefined,
          paymentMethodId: formData.paymentMethodId,
          projectId: formData.projectId || null,
          categories,
          checkbookId: formData.checkbookId || undefined, // For auto-incrementing check numbers
        };
        
        await updateOperation(operationId, updateData);
      } else {
        // Create mode: use IDs when available, names as fallback
        console.log('[OperationFormPage] Creating operation for account:', effectiveAccountId);
        const operationData = {
          direction: formData.direction,
          paymentDate: formData.paymentDate,
          accountingDate: formData.accountingDate || undefined,
          amount: parseFloat(formData.amount),
          counterpartyName: formData.counterpartyName.trim(),
          categoryName: formData.categoryName.trim() || undefined,
          paymentMethodName: formData.paymentMethodId ? undefined : (formData.paymentMethodName || 'Carte bancaire'),
          paymentMethodId: formData.paymentMethodId || undefined,
          paymentDetails: formData.paymentDetails.trim() || undefined,
          reference: formData.reference.trim() || undefined,
          comment: formData.comment.trim() || undefined,
          status: formData.status,
          projectId: formData.projectId || undefined,
          checkbookId: formData.checkbookId || undefined, // For auto-incrementing check numbers
        };
        console.log('[OperationFormPage] Operation data:', operationData);
        const createdOperation = await createOperation(effectiveAccountId, operationData);
        console.log('[OperationFormPage] Operation created successfully:', createdOperation?.id);
        
        // If this operation was created from reconciliation, mark the line as created
        if (createdOperation && reconciliationData) {
          try {
            console.log('[OperationFormPage] Marking reconciliation line as created:', reconciliationData.lineId);
            await reconciliationService.markLineCreated(
              reconciliationData.sessionId,
              reconciliationData.lineId,
              createdOperation.id
            );
            console.log('[OperationFormPage] Line marked as created successfully');
          } catch (matchError) {
            console.error('[OperationFormPage] Mark as created failed:', matchError);
            // Don't fail the whole operation if marking fails
          }
        }
      }
      
      // Navigate back (page is preserved in returnTo URL)
      const targetUrl = returnTo || `/accounts/${effectiveAccountId}/operations`;
      navigate(targetUrl);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof OperationFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleTransferChange = (field: keyof TransferFormData, value: string | boolean) => {
    setTransferData(prev => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleTransferModeToggle = (enabled: boolean) => {
    setIsTransferMode(enabled);
    setFormError(null);
    // Sync common fields
    if (enabled) {
      setTransferData(prev => ({
        ...prev,
        paymentDate: formData.paymentDate,
        accountingDate: formData.accountingDate,
        amount: formData.amount,
        reference: formData.reference,
        comment: formData.comment,
        status: formData.status,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        paymentDate: transferData.paymentDate,
        accountingDate: transferData.accountingDate,
        amount: transferData.amount,
        reference: transferData.reference,
        comment: transferData.comment,
        status: transferData.status,
      }));
    }
  };

  if (isLoadingOperation) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-500 dark:text-slate-400">{t('common.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!selectedAccount && !isLoading && !isLoadingOperation) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-500 dark:text-slate-400">{t('operations.selectAccount')}</p>
          </div>
        </Card>
      </div>
    );
  }

  // Get destination accounts (all accounts except current)
  const destinationAccounts = accounts.filter(a => a.id !== effectiveAccountId);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {isTransferMode 
            ? t('transfer.title')
            : isEditMode 
              ? t('operations.form.editTitle') 
              : t('operations.form.createTitle')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400">
          {selectedAccount?.name}
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error message */}
          {formError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-600 dark:text-red-400">
              {formError}
            </div>
          )}

          {/* Transfer Mode Toggle - only show in create mode with 2+ accounts */}
          {canTransfer && (
            <div className="flex items-center gap-3 p-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isTransferMode}
                  onChange={(e) => handleTransferModeToggle(e.target.checked)}
                  className="w-4 h-4 text-violet-600 rounded"
                />
                <span className="font-medium text-violet-900 dark:text-violet-100">
                  {t('transfer.toggle')}
                </span>
              </label>
              <span className="text-sm text-violet-600 dark:text-violet-400">
                {t('transfer.description')}
              </span>
            </div>
          )}

          {isTransferMode ? (
            /* Transfer Mode Form */
            <>
              {/* Destination Account */}
              <div>
                <label htmlFor="destinationAccountId" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('transfer.destinationAccount')} *
                </label>
                <select
                  id="destinationAccountId"
                  value={transferData.destinationAccountId}
                  onChange={(e) => handleTransferChange('destinationAccountId', e.target.value)}
                  disabled={isSaving}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                >
                  <option value="">{t('transfer.selectDestination')}</option>
                  {destinationAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <label htmlFor="transferAmount" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.amount')} *
                </label>
                <Input
                  id="transferAmount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={transferData.amount}
                  onChange={(e) => handleTransferChange('amount', e.target.value)}
                  placeholder="0.00"
                  disabled={isSaving}
                />
              </div>

              {/* Payment Date */}
              <div>
                <label htmlFor="transferPaymentDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.paymentDate')} *
                </label>
                <Input
                  id="transferPaymentDate"
                  type="date"
                  value={transferData.paymentDate}
                  onChange={(e) => handleTransferChange('paymentDate', e.target.value)}
                  disabled={isSaving}
                />
              </div>

              {/* Accounting Date */}
              <div>
                <label htmlFor="transferAccountingDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.accountingDate')}
                </label>
                <Input
                  id="transferAccountingDate"
                  type="date"
                  value={transferData.accountingDate}
                  onChange={(e) => handleTransferChange('accountingDate', e.target.value)}
                  disabled={isSaving}
                />
              </div>

              {/* Debit Category (for source account) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('transfer.debitCategory')} *
                </label>
                <CategorySelector
                  value={transferData.debitCategoryId}
                  onChange={(categoryId) => {
                    setTransferData(prev => ({ ...prev, debitCategoryId: categoryId || '' }));
                    setFormError(null);
                  }}
                  filterType="debit"
                  placeholder={t('categories.selector.placeholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Credit Category (for destination account) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('transfer.creditCategory')} *
                </label>
                <CategorySelector
                  value={transferData.creditCategoryId}
                  onChange={(categoryId) => {
                    setTransferData(prev => ({ ...prev, creditCategoryId: categoryId || '' }));
                    setFormError(null);
                  }}
                  filterType="credit"
                  placeholder={t('categories.selector.placeholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Payment Method - forced to Virement, read-only */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.paymentMethod')}
                </label>
                <div className="w-full px-4 py-2.5 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-400 cursor-not-allowed">
                  {t('transfer.paymentMethodVirement')}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t('transfer.paymentMethodFixed')}
                </p>
              </div>

              {/* Reference */}
              <div>
                <label htmlFor="transferReference" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.reference')}
                </label>
                <Input
                  id="transferReference"
                  type="text"
                  value={transferData.reference}
                  onChange={(e) => handleTransferChange('reference', e.target.value)}
                  placeholder={t('operations.form.referencePlaceholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Comment */}
              <div>
                <label htmlFor="transferComment" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.comment')}
                </label>
                <textarea
                  id="transferComment"
                  value={transferData.comment}
                  onChange={(e) => handleTransferChange('comment', e.target.value)}
                  placeholder={t('operations.form.commentPlaceholder')}
                  disabled={isSaving}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors resize-none"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('operations.form.status', 'Statut')}
                </label>
                <div className="flex gap-2">
                  {(['unpointed', 'pointed', 'reconciled'] as const).map((statusOption) => {
                    const isSelected = transferData.status === statusOption;
                    const labels: Record<typeof statusOption, string> = {
                      unpointed: t('operations.status.unpointed', 'Non pointé'),
                      pointed: t('operations.status.pointed', 'Pointé'),
                      reconciled: t('operations.status.reconciled', 'Rapproché'),
                    };
                    const colors: Record<typeof statusOption, string> = {
                      unpointed: isSelected ? 'bg-slate-500 text-white border-slate-500' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600',
                      pointed: isSelected ? 'bg-green-500 text-white border-green-500' : 'bg-white dark:bg-slate-700 text-green-600 dark:text-green-400 border-green-300 dark:border-green-600',
                      reconciled: isSelected ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-600',
                    };
                    return (
                      <button
                        key={statusOption}
                        type="button"
                        onClick={() => handleTransferChange('status', statusOption)}
                        disabled={isSaving}
                        className={`px-3 py-1.5 text-sm font-medium rounded border transition-colors ${colors[statusOption]} ${isSaving ? 'opacity-50' : ''}`}
                      >
                        {labels[statusOption]}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            /* Normal Operation Form */
            <>
              {/* Direction */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('operations.form.direction')} *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="direction"
                      value="debit"
                      checked={formData.direction === 'debit'}
                      onChange={() => handleChange('direction', 'debit')}
                      className="w-4 h-4 text-violet-600"
                    />
                    <span className="text-red-600 dark:text-red-400 font-medium">{t('operations.form.debit')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="direction"
                      value="credit"
                      checked={formData.direction === 'credit'}
                      onChange={() => handleChange('direction', 'credit')}
                      className="w-4 h-4 text-violet-600"
                    />
                    <span className="text-green-600 dark:text-green-400 font-medium">{t('operations.form.credit')}</span>
                  </label>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.amount')} *
                </label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="0.00"
                  disabled={isSaving}
                />
              </div>

              {/* Payment Date */}
              <div>
                <label htmlFor="paymentDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.paymentDate')} *
                </label>
                <Input
                  id="paymentDate"
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => handleChange('paymentDate', e.target.value)}
                  disabled={isSaving}
                />
              </div>

              {/* Accounting Date */}
              <div>
                <label htmlFor="accountingDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.accountingDate')}
                </label>
                <Input
                  id="accountingDate"
                  type="date"
                  value={formData.accountingDate}
                  onChange={(e) => handleChange('accountingDate', e.target.value)}
                  disabled={isSaving}
                />
              </div>

              {/* Counterparty */}
              <div>
                <label htmlFor="counterpartyName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.counterparty')} *
                </label>
                <CounterpartySelector
                  value={formData.counterpartyName}
                  onChange={(name, counterpartyId) => {
                    setFormData(prev => ({
                      ...prev,
                      counterpartyName: name,
                      counterpartyId: counterpartyId,
                    }));
                    setFormError(null);
                  }}
                  placeholder={t('operations.form.counterpartyPlaceholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Category */}
              <div>
                <label htmlFor="categoryName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.category')}
                </label>
                <CategorySelector
                  value={formData.categoryId}
                  onChange={(categoryId, categoryName) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      categoryId: categoryId,
                      categoryName: categoryName || ''
                    }));
                    setFormError(null);
                  }}
                  filterType={formData.direction === 'debit' ? 'debit' : 'credit'}
                  placeholder={t('categories.selector.placeholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Payment Method */}
              <div>
                <label htmlFor="paymentMethodId" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.paymentMethod')}
                </label>
                <select
                  id="paymentMethodId"
                  value={formData.paymentMethodId || ''}
                  onChange={(e) => {
                    const selectedPm = paymentMethods.find(pm => pm.id === e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      paymentMethodId: e.target.value,
                      paymentMethodName: selectedPm?.name || '',
                    }));
                  }}
                  disabled={isSaving}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                >
                  <option value="">{t('operations.form.paymentMethod', 'Mode de paiement')}</option>
                  {paymentMethods.map(pm => (
                    <option key={pm.id} value={pm.id}>
                      {pm.logoUrl} {pm.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Details (check number, card number, etc.) */}
              {(() => {
                // Check if selected payment method uses a checkbook
                const selectedPm = paymentMethods.find(pm => pm.id === formData.paymentMethodId);
                const usesCheckbook = selectedPm?.usesCheckbook ?? false;
                
                if (usesCheckbook && effectiveAccountId) {
                  return (
                    <CheckNumberInput
                      accountId={effectiveAccountId}
                      value={formData.paymentDetails}
                      onChange={(value, checkbookId) => {
                        setFormData(prev => ({
                          ...prev,
                          paymentDetails: value,
                          checkbookId: checkbookId,
                        }));
                      }}
                      disabled={isSaving}
                    />
                  );
                }
                
                return (
                  <div>
                    <label htmlFor="paymentDetails" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('operations.form.paymentDetails')}
                    </label>
                    <Input
                      id="paymentDetails"
                      type="text"
                      value={formData.paymentDetails}
                      onChange={(e) => handleChange('paymentDetails', e.target.value)}
                      placeholder={t('operations.form.paymentDetailsPlaceholder')}
                      disabled={isSaving}
                    />
                  </div>
                );
              })()}

              {/* Project (optional) */}
              <div>
                <label htmlFor="projectId" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.project')}
                </label>
                <div className="relative">
                  {/* Color indicator for selected project */}
                  {formData.projectId && (() => {
                    const selectedProject = projects.find(p => p.id === formData.projectId);
                    return selectedProject?.color ? (
                      <div
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full pointer-events-none"
                        style={{ backgroundColor: selectedProject.color }}
                      />
                    ) : null;
                  })()}
                  <select
                    id="projectId"
                    value={formData.projectId || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        projectId: e.target.value || undefined,
                      }));
                    }}
                    disabled={isSaving}
                    className={`w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors ${formData.projectId && projects.find(p => p.id === formData.projectId)?.color ? 'pl-8' : ''}`}
                  >
                    <option value="">{t('operations.form.noProject')}</option>
                    {projects
                      .filter(p => p.status === 'active' || p.id === formData.projectId)
                      .map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}{project.status !== 'active' ? ` (${t(`projects.status.${project.status}`)})` : ''}
                        </option>
                      ))}
                  </select>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t('operations.form.projectHelp')}
                </p>
              </div>

              {/* Reference */}
              <div>
                <label htmlFor="reference" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.reference')}
                </label>
                <Input
                  id="reference"
                  type="text"
                  value={formData.reference}
                  onChange={(e) => handleChange('reference', e.target.value)}
                  placeholder={t('operations.form.referencePlaceholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Comment */}
              <div>
                <label htmlFor="comment" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.comment')}
                </label>
                <textarea
                  id="comment"
                  value={formData.comment}
                  onChange={(e) => handleChange('comment', e.target.value)}
                  placeholder={t('operations.form.commentPlaceholder')}
                  disabled={isSaving}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors resize-none"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('operations.form.status', 'Statut')}
                </label>
                <div className="flex gap-2">
                  {(['unpointed', 'pointed', 'reconciled'] as const).map((statusOption) => {
                    const isSelected = formData.status === statusOption;
                    const labels: Record<typeof statusOption, string> = {
                      unpointed: t('operations.status.unpointed', 'Non pointé'),
                      pointed: t('operations.status.pointed', 'Pointé'),
                      reconciled: t('operations.status.reconciled', 'Rapproché'),
                    };
                    const colors: Record<typeof statusOption, string> = {
                      unpointed: isSelected ? 'bg-slate-500 text-white border-slate-500' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600',
                      pointed: isSelected ? 'bg-green-500 text-white border-green-500' : 'bg-white dark:bg-slate-700 text-green-600 dark:text-green-400 border-green-300 dark:border-green-600',
                      reconciled: isSelected ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-600',
                    };
                    return (
                      <button
                        key={statusOption}
                        type="button"
                        onClick={() => handleChange('status', statusOption)}
                        disabled={isSaving}
                        className={`px-3 py-1.5 text-sm font-medium rounded border transition-colors ${colors[statusOption]} ${isSaving ? 'opacity-50' : ''}`}
                      >
                        {labels[statusOption]}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(effectiveAccountId ? `/accounts/${effectiveAccountId}/operations` : '/accounts')}
              disabled={isSaving}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="flex-1"
            >
              {isSaving ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common.saving')}
                </span>
              ) : (
                isTransferMode
                  ? t('transfer.create')
                  : isEditMode 
                    ? t('common.save') 
                    : t('operations.form.create')
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default OperationFormPage;
